import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTicketService, type TicketListFilters } from '../services/ticketService';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';
import type { TicketView } from '../../../types/backend';

interface UseTicketListResult {
  tickets: TicketView[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const ts = (value?: string): number => {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Most recently updated first (falls back to created). */
const sortByRecency = (list: TicketView[]): TicketView[] =>
  [...list].sort((a, b) => ts(b.updated_at ?? b.created_at) - ts(a.updated_at ?? a.created_at));

// Query keys for the itsm feature (server reads: the ticket list per customer,
// and each ticket's detail). The list is keyed by customer + filters so a scope
// or filter change is a distinct cache entry; the detail is keyed by ticket so
// reopening one is instant.
export const ticketKeys = {
  list: (customerId: string | undefined, filters?: TicketListFilters) =>
    ['itsm', 'list', customerId, filters] as const,
  detail: (customerId: string | undefined, ticketId: string | null) =>
    ['itsm', 'detail', customerId, ticketId] as const,
};

export function useTicketList(customerId?: string, filters?: TicketListFilters): UseTicketListResult {
  const ticketService = useTicketService();

  const query = useQuery({
    queryKey: ticketKeys.list(customerId, filters),
    queryFn: async () => {
      const list = await ticketService.listTickets(customerId as string, filters);
      return sortByRecency(list);
    },
    enabled: !!customerId,
  });

  const { refetch } = query;
  const reload = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    tickets: query.data ?? [],
    loading: query.isFetching,
    error: query.error ? extractErrorMessage(query.error, 'Failed to load tickets') : null,
    reload,
  };
}
